import type { ImageAssetType, ServiceType } from '@/components/ui/carousel';
import type { AssetRef, AssetCartItem } from '@/components/orders/cart/types/cart.types';
import { productServiceToServiceInfo } from '@/components/orders/cart/utils/cartTransforms';
import type { OrderRequest, SavedCard } from '@/types/order.types';
import { getServiceUnitPrice } from '@/util/service-pricing';
import { buildOrderPayment } from '@/util/orderPayment';

const assetUrlOf = (img: ImageAssetType) =>
  img.caches?.['1920w'] || img.caches?.fullSize || '';

export function toAssetRef(img: ImageAssetType): AssetRef {
  return {
    assetId: img.assetUUID,
    photosetUUID: img.photosetUUID,
    assetUrl: assetUrlOf(img),
  };
}

/**
 * DV-3180: `orderedBy` is what the orders list renders in its "Ordered By" column, and this
 * builder — the modal's "order now" path — was the one order-creation path that never sent it,
 * so every edit request placed this way showed a blank. The cart path has always set it
 * (`cartTransforms.transformBulkOrderCartData`); this brings the direct path in line.
 *
 * order-service now also falls back to the caller's token when the field is blank, which is the
 * real fix and covers any other client. This stays because the token fallback resolves a Keycloak
 * name, while the cart sends this same localStorage value — sending it here keeps the two Connect
 * paths writing an identical string for the same user, rather than two spellings of one name.
 */
export function buildAssetOrder(
  photos: ImageAssetType[],
  service: ServiceType,
  note: string,
  _propertyAddress: string,
  card: SavedCard | null,
  promoCodeId?: string | null,
): OrderRequest {
  const assetRefs = photos.map(toAssetRef);
  const first = photos[0];
  const order: OrderRequest = {
    propertyUUID: first?.propertyUUID,
    services: [{ shoppingCartSlug: service.shoppingCartSlug, quantity: assetRefs.length }],
    assetRefs,
    assetNote: note.trim(),
    sourceOrderUUID: first?.orderUUID,
    orderedBy: localStorage.getItem('userName'),
    ...(promoCodeId ? { promocode: promoCodeId } : {}),
  };
  if (!card) return order;
  return {
    ...order,
    cardNumber: card.cardNumber,
    cardType: card.cardType,
    payment: buildOrderPayment(card),
  };
}

export function buildAssetCartItem(
  photos: ImageAssetType[],
  service: ServiceType,
  note: string,
  propertyAddress: string,
): AssetCartItem {
  const assetRefs = photos.map(toAssetRef);
  const unitPrice = getServiceUnitPrice(service);
  const first = photos[0];
  return {
    service: productServiceToServiceInfo(service),
    assetRefs,
    sourceOrderUUID: first?.orderUUID,
    additionalNotes: note.trim(),
    price: unitPrice * assetRefs.length, // optimistic; server is authoritative on refetch
    assetId: assetRefs[0]?.assetId ?? '', // server backfills/uses as de-dup key
    assetUrl: assetRefs[0]?.assetUrl ?? '',
    propertyUUID: first?.propertyUUID ?? '',
    propertyAddress,
    ...(first?.photosetUUID ? { photosetUUID: first.photosetUUID } : {}),
  };
}
